import React, { useState, useEffect } from "react";
import { useNotification } from "../../context/NotificationContext";
import { ContactPageSettingServices } from "../../services/admin/ContactPageSettingServices";
import {
    Save,
    Mail,
    Phone,
    Quote,
    Instagram,
    Twitter,
    Facebook,
    Youtube,
    Edit2,
    X,
    Loader2
} from "lucide-react";

export default function ContactPageSetting() {
    const { showNotification } = useNotification();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);

    const [formData, setFormData] = useState({
        email: "",
        booking_email: "",
        phone: "",
        contact_quote: "",
        instagram: "",
        twitter: "",
        facebook: "",
        youtube: ""
    });

    const [originalData, setOriginalData] = useState({});

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setIsLoading(true);
                const response = await ContactPageSettingServices.getSettings();
                if (response.status === "success") {
                    setFormData(response.data);
                    setOriginalData(response.data);
                }
            } catch (error) {
                showNotification({
                    type: "failed",
                    title: "Load Error",
                    message: "Failed to fetch contact settings."
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchSettings();
    }, [showNotification]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const { email, booking_email, phone, contact_quote } = formData;

        if (!email || !booking_email || !phone || !contact_quote) {
            showNotification({
                type: "error",
                title: "Validation Error",
                message: "All important fields (Email, Booking Email, Phone, and Quote) are required."
            });
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showNotification({ type: "error", title: "Validation Error", message: "Please enter a valid Public Email address." });
            return false;
        }
        if (!emailRegex.test(booking_email)) {
            showNotification({ type: "error", title: "Validation Error", message: "Please enter a valid Booking Email address." });
            return false;
        }

        return true;
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setIsSaving(true);
        try {
            const response = await ContactPageSettingServices.updateSettings(formData);
            if (response.status === "success") {
                showNotification({ type: "success", title: "Settings Saved", message: "Your contact settings have been successfully updated." });
                setOriginalData(formData);
                setIsEditing(false);
            } else {
                showNotification({ type: "error", title: "Error", message: response.message || "Failed to update settings." });
            }
        } catch (error) {
            showNotification({ type: "error", title: "Error", message: "An unexpected error occurred." });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData(originalData);
        setIsEditing(false);
    };

    const inputClasses = "w-full pl-12 pr-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-[#222] text-sm focus:border-jazz-brown outline-none transition-colors disabled:opacity-75 disabled:cursor-not-allowed";
    const labelClasses = "block text-xs font-bold text-black dark:text-white uppercase tracking-widest mb-2";

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="animate-spin text-jazz-brown" size={48} />
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-10 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="text-3xl lg:text-4xl font-normal text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                        Contact Page Settings
                    </h1>
                    <p className="text-black dark:text-white/50 text-sm font-serif">
                        Manage your contact information, booking details, and social links for the public contact page.
                    </p>
                </div>
                {!isEditing && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-6 py-2.5 bg-black text-white rounded-xl hover:bg-jazz-brown transition-colors text-xs font-bold tracking-widest uppercase"
                    >
                        <Edit2 size={16} />
                        Edit Settings
                    </button>
                )}
            </div>

            <div className="bg-white dark:bg-[#1A1A1A] p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                <div className="flex items-center gap-3 mb-8 pb-4 border-b border-black/5 dark:border-white/5">
                    <div className="p-3 bg-jazz-brown/10 text-jazz-brown rounded-xl">
                        <Mail size={24} />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>Core Information</h2>
                        <p className="text-xs text-black dark:text-white/50 uppercase tracking-widest mt-1">Primary details visible on the public page</p>
                    </div>
                </div>

                <form onSubmit={handleSave} className="space-y-8">
                    {/* Basic Contact Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className={labelClasses}>Public Email Address <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={18} />
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email || ""}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={inputClasses}
                                    placeholder="contact@example.com"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className={labelClasses}>Booking Email Address <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={18} />
                                <input
                                    type="email"
                                    name="booking_email"
                                    value={formData.booking_email || ""}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={inputClasses}
                                    placeholder="bookings@example.com"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <label className={labelClasses}>Contact Phone Number <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={18} />
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone || ""}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={inputClasses}
                                    placeholder="+1 234 567 8900"
                                    required
                                />
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelClasses}>Contact Page Quote <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <Quote className="absolute left-4 top-4 text-black/40" size={18} />
                                <textarea
                                    name="contact_quote"
                                    value={formData.contact_quote || ""}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    className={`${inputClasses} pl-12 resize-none h-24`}
                                    placeholder="Enter a professional quote or message for the contact page..."
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Social Links */}
                    <div>
                        <h3 className="text-sm font-bold text-black dark:text-white uppercase tracking-widest mb-6 pb-2 border-b border-black/5 inline-block">Social Media Links (Optional)</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className={labelClasses}>Instagram URL</label>
                                <div className="relative">
                                    <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={18} />
                                    <input
                                        type="url"
                                        name="instagram"
                                        value={formData.instagram || ""}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        className={inputClasses}
                                        placeholder="https://instagram.com/..."
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={labelClasses}>Twitter / X URL</label>
                                <div className="relative">
                                    <Twitter className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={18} />
                                    <input
                                        type="url"
                                        name="twitter"
                                        value={formData.twitter || ""}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        className={inputClasses}
                                        placeholder="https://twitter.com/..."
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={labelClasses}>Facebook URL</label>
                                <div className="relative">
                                    <Facebook className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={18} />
                                    <input
                                        type="url"
                                        name="facebook"
                                        value={formData.facebook || ""}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        className={inputClasses}
                                        placeholder="https://facebook.com/..."
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={labelClasses}>YouTube Channel</label>
                                <div className="relative">
                                    <Youtube className="absolute left-4 top-1/2 -translate-y-1/2 text-black/40" size={18} />
                                    <input
                                        type="url"
                                        name="youtube"
                                        value={formData.youtube || ""}
                                        onChange={handleChange}
                                        disabled={!isEditing}
                                        className={inputClasses}
                                        placeholder="https://youtube.com/..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {isEditing && (
                        <div className="flex flex-col-reverse sm:flex-row justify-end gap-4 pt-6 mt-6 border-t border-black/5 dark:border-white/5">
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="flex items-center justify-center gap-2 px-8 py-3 bg-gray-100 text-black rounded-xl hover:bg-gray-200 transition-colors text-xs font-bold tracking-widest uppercase w-full sm:w-auto"
                            >
                                <X size={16} />
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="flex items-center justify-center gap-2 px-8 py-3 bg-black text-white rounded-xl hover:bg-jazz-brown transition-colors text-xs font-bold tracking-widest uppercase disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        Update Settings
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}
